import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import React, { useState } from 'react';
import { translate } from '../../../common/intl';
import { Dialog } from '../../components/dialogs';
import { Autocomplete, TextField } from '../../components/forms/fields-next';
import useStyles from './styles';

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (proposedNumOfDocDigits: number, proposedStartingNumber: number) => void
  onCancel: () => void
  startingNumber: number
  numOfDocDigits: number
  docTypePrefix: string
}

enum SolutionOption {
  IncreaseDigits = 'IncreaseDigits',
  UpdateStartingNumber = 'UpdateStartingNumber',
  UseCurrentSettings = 'UseCurrentSettings',
}

interface SolutionOptionGeneratorParams {
  numOfDocDigits: number
  proposedNumOfDocDigits: number
  startingNumber: number
  proposedStartingNumber: number
  docTypePrefix: string
}

interface SolutionOptionValueGeneratorParams extends Pick<SolutionOptionGeneratorParams, 'proposedNumOfDocDigits' | 'proposedStartingNumber'> {
  setProposedNumOfDocDigits: (value: number) => void
  setProposedStartingNumber: (value: number) => void
}

interface SolutionOptionParams {
  label: string
  value: SolutionOption
  exampleIdGenerator: (params: SolutionOptionGeneratorParams) => string
  valueGenerator?: (params: SolutionOptionValueGeneratorParams) => JSX.Element
}

const SOLUTION_OPTIONS: SolutionOptionParams[] = [
  {
    label: 'documenttype.numbering.validation.option.increase.digits',
    value: SolutionOption.IncreaseDigits,
    valueGenerator: ({
      proposedNumOfDocDigits,
      setProposedNumOfDocDigits,
    }) => (
      <Autocomplete
        options={Array.from({ length: 15 }, (_, i) => i + 1)}
        value={proposedNumOfDocDigits}
        getOptionLabel={(option: number) => option.toString()}
        onChange={(_, value) => setProposedNumOfDocDigits(value)}
        size="small"
        disableClearable
      />
    ),
    exampleIdGenerator: ({
      proposedNumOfDocDigits,
      startingNumber,
      docTypePrefix,
    }) => `${docTypePrefix}${startingNumber.toString().padStart(proposedNumOfDocDigits, '0')}`,
  },
  {
    label: 'documenttype.numbering.validation.option.update.starting.number',
    value: SolutionOption.UpdateStartingNumber,
    exampleIdGenerator: ({
      numOfDocDigits,
      proposedStartingNumber,
      docTypePrefix,
    }) => `${docTypePrefix}${proposedStartingNumber.toString().padStart(numOfDocDigits, '0')}`,
    valueGenerator: ({
      proposedStartingNumber,
      setProposedStartingNumber,
    }) => (
      <TextField
        type="number"
        value={proposedStartingNumber}
        onChange={(e) => setProposedStartingNumber(Number(e.target.value))}
        size="small"
      />
    ),
  },
  {
    label: 'documenttype.numbering.validation.option.use.current.settings',
    value: SolutionOption.UseCurrentSettings,
    exampleIdGenerator: ({
      numOfDocDigits,
      startingNumber,
      docTypePrefix,
    }) => `${docTypePrefix}${startingNumber.toString().padStart(numOfDocDigits, '0')}`,
  },
];

const ValidationDialog: React.FC<Props> = ({
  open,
  startingNumber,
  numOfDocDigits,
  docTypePrefix,
  onClose,
  onConfirm,
  onCancel,
}) => {
  const classes = useStyles();

  const [selectedOption, setSelectedOption] = useState<SolutionOption>(SolutionOption.IncreaseDigits);
  const [proposedStartingNumber, setProposedStartingNumber] = useState(() => Number(startingNumber.toString().slice(0, -numOfDocDigits)));
  const [proposedNumOfDocDigits, setProposedNumOfDocDigits] = useState(() => startingNumber.toString().length);

  const expectedDocId = `${docTypePrefix}${startingNumber.toString().padStart(numOfDocDigits, '0')}`;

  return (
    <Dialog
      open={open}
      title={translate('documenttype.numbering.validation.title')}
      onClose={onClose}
      confirmLabel={translate('documenttype.numbering.validation.confirm')}
      cancelLabel={translate('common.cancel')}
      onConfirm={() => onConfirm(
        selectedOption === SolutionOption.IncreaseDigits ? proposedNumOfDocDigits : numOfDocDigits,
        selectedOption === SolutionOption.UpdateStartingNumber ? proposedStartingNumber : startingNumber,
      )}
      onCancel={onCancel}
    >
      <div className={classes.numberingValidationMessage}>
        <div
          dangerouslySetInnerHTML={{
            __html: translate('documenttype.numbering.validation.message', {
              startingNumber,
              numOfDocDigits,
              docId: expectedDocId,
            }),
          }}
        />
        <div className={classes.numberingValidationOptions}>
          {SOLUTION_OPTIONS.map((option) => (
            <div
              className={cx(
                classes.numberingValidationOption,
                { [classes.numberingValidationOptionSelected]: selectedOption === option.value },
              )}
              onClick={() => setSelectedOption(option.value)}
              key={option.value}
            >
              <FontAwesomeIcon
                icon={selectedOption === option.value ? solid('check-circle') : regular('circle')}
                size="lg"
                className={cx(
                  classes.numberingValidationOptionIcon,
                  { [classes.numberingValidationOptionIconSelected]: selectedOption === option.value },
                )}
              />
              <div className={classes.numberingValidationOptionText}>
                {translate(option.label)}
                <div className={classes.numberingValidationOptionValue}>
                  {option.valueGenerator?.({
                    proposedNumOfDocDigits,
                    setProposedNumOfDocDigits,
                    proposedStartingNumber,
                    setProposedStartingNumber,
                  })}
                </div>
                <div className={classes.numberingValidationMessageExample}>
                  {translate('documenttype.numbering.validation.example.id')}
                  <code>{option.exampleIdGenerator({
                    numOfDocDigits,
                    proposedNumOfDocDigits,
                    startingNumber,
                    proposedStartingNumber,
                    docTypePrefix,
                  })}</code>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
};

export default ValidationDialog;
